import React, { Suspense, lazy, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Chat } from './components/Chat';
import { AuthButton } from './components/AuthButton';
import { useQuery, useConvexAuth } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { useEnsureUser } from './hooks/useAuth';
import { languageNames, type SupportedLanguage } from '@/i18n/index';
import { isAuth0Configured } from './config/auth0';
import designShot1 from '../../Design/1.png';
import designShot2 from '../../Design/2.png';
import designShot3 from '../../Design/3.png';
import designShot4 from '../../Design/4.png';
import './App.css';

const MAIN_LANGUAGES: SupportedLanguage[] = ['bs', 'en', 'de', 'fr', 'sk', 'it', 'mk', 'ru', 'es'];

const LazyAnalyticsDashboard = lazy(() =>
  import('./components/AnalyticsDashboard').then((module) => ({ default: module.AnalyticsDashboard }))
);

const LazyAgentDashboard = lazy(() =>
  import('./components/dashboard/AgentDashboard').then((module) => ({ default: module.AgentDashboard }))
);

const LazyBulkDocumentClassifier = lazy(() =>
  import('./components/BulkDocumentClassifier').then((module) => ({ default: module.BulkDocumentClassifier }))
);

const LazyAdminShell = lazy(() =>
  import('./components/admin/AdminShell').then((module) => ({ default: module.AdminShell }))
);

function AppViewFallback({ label }: { label: string }) {
  return (
    <div style={{ padding: '32px', textAlign: 'center', color: '#64748b' }}>
      Ucitavam {label}...
    </div>
  );
}

/**
 * Wait until Convex has received and accepted the Auth0 identity.
 * Auth0 UI login alone is not enough; authenticated Convex queries require
 * ctx.auth.getUserIdentity() to be available server-side.
 */
function ConvexAuthReadyGate({ children }: { children: React.ReactNode }) {
  const { isLoading, isAuthenticated } = useConvexAuth();

  if (isLoading) {
    return (
      <div className="app-shell" data-testid="convex-auth-loading">
        <div className="app-shell__overlay" aria-hidden />
        <div className="app-shell__content" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh'
        }}>
          <div className="auth-spinner" style={{
            width: '48px',
            height: '48px',
            border: '4px solid #e0e0e0',
            borderTopColor: '#1e8fb9',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <p style={{ marginTop: '16px', color: '#64748b' }}>
            Učitavanje sigurnosne Convex sesije...
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="app-shell" data-testid="convex-auth-error">
        <div className="app-shell__overlay" aria-hidden />
        <div className="app-shell__content" style={{ padding: '32px', maxWidth: 860, margin: '0 auto' }}>
          <h1 style={{ marginBottom: 12, color: '#dc2626' }}>⚠️ Convex autentifikacija nije spremna</h1>
          <p style={{ marginBottom: 8, color: '#2b4a66' }}>
            Auth0 prijava je završena, ali Convex backend još ne prima validan server-side identitet.
          </p>
          <p style={{ color: '#64748b', fontSize: '14px' }}>
            Provjeriti Auth0 token handoff i Convex auth.config.ts.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

/**
 * Global authentication guard - requires login for entire app
 */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, loginWithRedirect, error } = useAuth0();

  // If Auth0 is not configured, show configuration error
  if (!isAuth0Configured()) {
    return (
      <div className="app-shell">
        <div className="app-shell__overlay" aria-hidden />
        <div className="app-shell__content" style={{ padding: '32px', maxWidth: 860, margin: '0 auto' }}>
          <h1 style={{ marginBottom: 12, color: '#dc2626' }}>⚠️ Auth0 nije konfigurisan</h1>
          <p style={{ marginBottom: 8, color: '#2b4a66' }}>
            Autentifikacija nije dostupna. Molimo kontaktirajte administratora.
          </p>
          <p style={{ color: '#64748b', fontSize: '14px' }}>
            Potrebno je postaviti VITE_AUTH0_DOMAIN i VITE_AUTH0_CLIENT_ID environment varijable.
          </p>
        </div>
      </div>
    );
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className="app-shell">
        <div className="app-shell__overlay" aria-hidden />
        <div className="app-shell__content" style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '100vh' 
        }}>
          <div className="auth-spinner" style={{
            width: '48px',
            height: '48px',
            border: '4px solid #e0e0e0',
            borderTopColor: '#1e8fb9',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <p style={{ marginTop: '16px', color: '#64748b' }}>Učitavanje...</p>
        </div>
      </div>
    );
  }

  // Show error if Auth0 failed
  if (error) {
    // Handle "Invalid state" error - clean URL and allow retry
    const handleRetry = () => {
      // Clean up any auth parameters from URL
      const cleanUrl = window.location.origin + window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
      
      // Clear Auth0 cached state that might be stale
      try {
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (key.startsWith('a0.spajs.txs') || key.startsWith('@@auth0spajs@@'))) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));
      } catch (e) {
        console.warn('Could not clear Auth0 cache:', e);
      }
      
      // Now redirect to login
      loginWithRedirect();
    };

    const isInvalidState = error.message?.toLowerCase().includes('invalid state');
    
    return (
      <div className="app-shell">
        <div className="app-shell__overlay" aria-hidden />
        <div className="app-shell__content" style={{ padding: '32px', maxWidth: 860, margin: '0 auto' }}>
          <h1 style={{ marginBottom: 12, color: '#dc2626' }}>❌ Greška autentifikacije</h1>
          <p style={{ marginBottom: 8, color: '#2b4a66' }}>
            {isInvalidState 
              ? 'Sesija je istekla ili je došlo do prekida u procesu prijave.'
              : `Došlo je do greške prilikom prijave: ${error.message}`
            }
          </p>
          {isInvalidState && (
            <p style={{ marginBottom: 16, color: '#64748b', fontSize: '14px' }}>
              Ovo se može desiti ako ste već prijavljeni u drugom prozoru ili ste koristili "Back" dugme.
            </p>
          )}
          <button
            onClick={handleRetry}
            style={{
              marginTop: '16px',
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #1e8fb9 0%, #186f90 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            🔄 Pokušaj ponovo
          </button>
        </div>
      </div>
    );
  }

  // If not authenticated, show login page
  if (!isAuthenticated) {
    return (
      <div className="app-shell">
        <div className="app-shell__overlay" aria-hidden />
        <div className="app-shell__content login-page">
          <div className="login-page__content">
            <span style={{ fontSize: '72px', marginBottom: '24px' }}>🏛️</span>
            <h1 style={{ 
              fontSize: '32px', 
              fontWeight: '700', 
              color: '#1e3a5f', 
              marginBottom: '12px' 
            }}>
              Carinski Asistent
            </h1>
            <p style={{ 
              fontSize: '18px', 
              color: '#64748b', 
              marginBottom: '32px',
              maxWidth: '400px'
            }}>
              Brza i precizna HS klasifikacija proizvoda.
              <br />
              Prijavite se za pristup aplikaciji.
            </p>
            <button
              data-testid="login-button"
              onClick={() => loginWithRedirect()}
              style={{
                padding: '16px 40px',
                background: 'linear-gradient(135deg, #1e8fb9 0%, #186f90 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '18px',
                fontWeight: '600',
                boxShadow: '0 4px 16px rgba(30, 143, 185, 0.4)',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(30, 143, 185, 0.5)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(30, 143, 185, 0.4)';
              }}
            >
              🔐 Prijavi se
            </button>
            <p style={{ 
              marginTop: '24px', 
              fontSize: '14px', 
              color: '#94a3b8' 
            }}>
              Potreban je korisnički račun za pristup.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // User is authenticated, render children
  return <>{children}</>;
}

function App() {
  return (
    <ConvexAuthReadyGate>
      <AuthenticatedAppContent />
    </ConvexAuthReadyGate>
  );
}

/**
 * Authenticated app content — rendered inside ConvexProviderWithAuth0 + ConvexAuthReadyGate
 * so that useQuery(api.adminAuth.isOwner) has valid Convex context.
 */
function AuthenticatedAppContent() {
  // Ensure every authenticated Auth0 user has a users/tenants mapping.
  useEnsureUser();

  const [view, setView] = useState<'chat' | 'analytics' | 'agent-monitoring' | 'admin-panel' | 'bulk-documents'>('chat');
  const [lang, setLang] = useState<SupportedLanguage>('bs');
  const ownerStatus = useQuery(api.adminAuth.isOwner);
  const isOwner = ownerStatus?.isOwner ?? false;
  const designShots = [designShot1, designShot2, designShot3, designShot4];

  return (
    <div className="app-shell" data-testid="authenticated-shell">
      <div className="app-shell__overlay" aria-hidden />
      <div className="app-shell__content">
        <nav className="app-shell__nav" data-testid="app-nav">
          <button
            onClick={() => setView('chat')}
            className={`app-shell__tab ${view === 'chat' ? 'is-active' : ''}`}
            data-testid="nav-klasifikacija"
          >
            💬 Klasifikacija
          </button>
          <button
            onClick={() => setView('analytics')}
            className={`app-shell__tab ${view === 'analytics' ? 'is-active' : ''}`}
          >
            📊 Analytics
          </button>
          <button
            onClick={() => setView('agent-monitoring')}
            className={`app-shell__tab ${view === 'agent-monitoring' ? 'is-active' : ''}`}
          >
            🤖 Agent Monitoring
          </button>
          <button
            onClick={() => setView('bulk-documents')}
            className={`app-shell__tab ${view === 'bulk-documents' ? 'is-active' : ''}`}
          >
            📁 Bulk dokumenti
          </button>
          {isOwner && (
            <button
              onClick={() => setView('admin-panel')}
              className={`app-shell__tab ${view === 'admin-panel' ? 'is-active' : ''}`}
            >
              ⚙️ Admin Panel
            </button>
          )}
          <select
            value={lang}
            onChange={(e) => setLang(e.target.value as SupportedLanguage)}
            className="app-shell__lang"
            aria-label="Language"
          >
            {MAIN_LANGUAGES.map((code) => (
              <option key={code} value={code}>
                🌐 {languageNames[code]}
              </option>
            ))}
          </select>
          <div className="app-shell__auth">
            <AuthButton variant="full" />
          </div>
        </nav>

        <section className="app-shell__design-preview" aria-label="Design Preview">
          {designShots.map((shot, index) => (
            <img key={shot} src={shot} alt={`Design preview ${index + 1}`} className="app-shell__design-image" />
          ))}
        </section>

        {view === 'chat'
          ? <Chat lang={lang} />
          : view === 'analytics'
            ? (
              <Suspense fallback={<AppViewFallback label="analytics modul" />}>
                <LazyAnalyticsDashboard />
              </Suspense>
            )
            : view === 'agent-monitoring'
              ? (
                <Suspense fallback={<AppViewFallback label="agent monitoringa" />}>
                  <LazyAgentDashboard />
                </Suspense>
              )
              : view === 'admin-panel'
                ? (
                  <Suspense fallback={<AppViewFallback label="admin panela" />}>
                    <LazyAdminShell />
                  </Suspense>
                )
                : (
                  <Suspense fallback={<AppViewFallback label="bulk dokumenata" />}>
                    <LazyBulkDocumentClassifier />
                  </Suspense>
                )}
      </div>
    </div>
  );
}

/**
 * Main App export - wrapped with RequireAuth to force login
 */
function AppWithAuth() {
  return (
    <RequireAuth>
      <App />
    </RequireAuth>
  );
}

export default AppWithAuth;
